'use strict';

/**
 * User Utility Links — dropdown toggle behavior.
 *
 * Manages open/close for Help and Account dropdown menus via click, blur,
 * focus, outside-click, and Escape key. Only one dropdown may be open at a
 * time — opening one automatically closes the other. Uses AbortController
 * for clean event listener lifecycle on outside-click handlers.
 */

/**
 * @type {string}
 * @private
 */
const _OPEN_CLASS = 'simpleDropdown--open';

/**
 * @type {{ buttonId: string, dropdownId: string }[]}
 * @private
 */
const _DROPDOWN_PAIRS = [
    { buttonId: 'help-button', dropdownId: 'help-dropdown' },
    { buttonId: 'account-button', dropdownId: 'account-dropdown' },
];

/**
 * Wires toggle behavior for a single utility dropdown pair.
 *
 * @param {{ buttonId: string, dropdownId: string }} pair - Button and dropdown IDs.
 * @param {number} index - Index within the registered dropdown list.
 * @param {{ close: () => void }[]} dropdownInstances - Shared mutual-exclusion registry.
 * @private
 */
function _initDropdownPair(pair, index, dropdownInstances) {
    const button = document.getElementById(pair.buttonId);
    const dropdown = document.getElementById(pair.dropdownId);

    if (!button || !dropdown) return;

    let outsideClickAbortController = null;

    /**
     * @returns {boolean}
     * @private
     */
    function _isOpen() {
        return dropdown.classList.contains(_OPEN_CLASS);
    }

    /**
     * @private
     */
    function _closeOtherDropdowns() {
        dropdownInstances.forEach((instance, i) => {
            if (i !== index) instance.close();
        });
    }

    /**
     * @private
     */
    function _open() {
        _closeOtherDropdowns();
        dropdown.classList.add(_OPEN_CLASS);
        button.setAttribute('aria-expanded', 'true');
        dropdown.setAttribute('aria-hidden', 'false');
        dropdown.focus();
    }

    /**
     * @private
     */
    function _close() {
        dropdown.classList.remove(_OPEN_CLASS);
        button.setAttribute('aria-expanded', 'false');
        dropdown.setAttribute('aria-hidden', 'true');

        if (outsideClickAbortController) {
            outsideClickAbortController.abort();
            outsideClickAbortController = null;
        }
    }

    dropdownInstances.push({ close: _close });

    button.addEventListener('click', () => {
        if (_isOpen()) {
            _close();
        } else {
            _open();
            outsideClickAbortController = new AbortController();
            document.addEventListener(
                'mousedown',
                (e) => {
                    if (
                        !dropdown.contains(e.target) &&
                        !button.contains(e.target)
                    ) {
                        _close();
                    }
                },
                { signal: outsideClickAbortController.signal },
            );
        }
    });

    button.addEventListener('focus', (e) => {
        if (
            _isOpen() &&
            (!e.relatedTarget || !dropdown.contains(e.relatedTarget))
        ) {
            _close();
        }
    });

    button.addEventListener('blur', (e) => {
        if (
            _isOpen() &&
            (!e.relatedTarget ||
                (!dropdown.contains(e.relatedTarget) &&
                    e.relatedTarget !== dropdown))
        ) {
            _close();
        }
    });

    dropdown.addEventListener('blur', (e) => {
        if (
            !e.relatedTarget ||
            (!e.currentTarget.contains(e.relatedTarget) &&
                !button.contains(e.relatedTarget))
        ) {
            _close();
        }
    });

    dropdown.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            _close();
            button.focus();
        }
    });
}

/**
 * Initializes Help and Account utility dropdown menus.
 */
export function initUserUtilityLinks() {
    /** @type {{ close: () => void }[]} */
    const dropdownInstances = [];

    _DROPDOWN_PAIRS.forEach((pair, index) => {
        _initDropdownPair(pair, index, dropdownInstances);
    });
}
